import { apiGet, apiPost, apiPatch, apiDelete } from '../../utilities/apiClient';
import { getSessionIds } from './_sessionIds';
import { ADD_VAULT, FETCHING_VAULT, FETCHED_VAULT } from '../constants';

export const fetchVaultEntries = (projectId: number | string) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId || projectId == null) return;
  dispatch({ type: FETCHING_VAULT, payload: true });
  try {
    const res = await apiGet(`/credentials/get-all/${projectId}/${accountId}/${cognitoId}`);
    const data = res?.payload ?? res?.data ?? res;
    const list = Array.isArray(data) ? data : [];
    dispatch({ type: ADD_VAULT, payload: { project_id: String(projectId), list } });
    dispatch({ type: FETCHED_VAULT, payload: { project_id: String(projectId) } });
  } catch {
    dispatch({ type: ADD_VAULT, payload: { project_id: String(projectId), list: [] } });
    dispatch({ type: FETCHED_VAULT, payload: { project_id: String(projectId) } });
  } finally {
    dispatch({ type: FETCHING_VAULT, payload: false });
  }
};

export const fetchVaultSalt = async (
  projectId: number | string,
  accountId: string,
  cognitoId: string,
): Promise<{ salt: string | null; status?: number; message?: string }> => {
  try {
    const res = await apiGet(`/credentials/vault-salt/${projectId}/${accountId}/${cognitoId}`);
    // apiGet returns { success:false, status, message } on a non-2xx. Surface
    // the status so the UI can tell the truth (403 = no Vault permission)
    // instead of masking every failure as a generic error.
    if (res?.success === false) return { salt: null, status: res.status, message: res.message };
    return { salt: res?.payload?.salt ?? null };
  } catch (err: any) {
    return { salt: null, message: err?.message };
  }
};

export const createVaultEntry = (
  projectId: number | string,
  credential: object,
) => async (): Promise<{ success: boolean; payload?: any; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const res = await apiPost(
      `/credentials/create/${projectId}/${accountId}/${cognitoId}`,
      { credential },
    );
    return { success: res?.success === true, payload: res?.payload, message: res?.message };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const updateVaultEntry = (
  credentialId: number,
  updates: object & { changed_fields?: string[] },
) => async (): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const res = await apiPatch(
      `/credentials/update/${credentialId}/${accountId}/${cognitoId}`,
      { updates },
    );
    return { success: res?.success === true, message: res?.message };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const deleteVaultEntry = (
  credentialId: number,
) => async (): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const res = await apiDelete(
      `/credentials/delete/${credentialId}/${accountId}/${cognitoId}`,
    );
    return { success: res?.success === true, message: res?.message };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const toggleVaultWatchShared = (
  credentialId: number,
  watchShared: boolean,
) => async (dispatch: any): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const res = await apiPatch(
      `/credentials/toggle-watch-shared/${credentialId}/${accountId}/${cognitoId}`,
      { watch_shared: watchShared },
    );
    if (res?.success) {
      // Optimistically update the Redux list so the UI reflects the change immediately
      // without a full refetch.
      dispatch({ type: 'UPDATE_VAULT_ENTRY', payload: { id: credentialId, watch_shared: watchShared } });
    }
    return { success: res?.success === true, message: res?.message };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export interface CredentialShareData {
  encrypted_data: string;
  iv: string;
  auth_tag: string;
  label: string;
  url?: string | null;
  username_hint?: string | null;
}

export const shareVaultEntry = (
  projectId: number | string,
  shareData: CredentialShareData,
  options: { expires_in?: number; max_uses?: number },
) => async (): Promise<{ success: boolean; code?: string; short_url?: string; expires_at?: number; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const res = await apiPost(`/links/create/${accountId}/${cognitoId}`, {
      project_id: projectId,
      target_type: 'credential_share',
      target_id: `share:${projectId}`,
      credential_share_data: shareData,
      expires_in: options.expires_in,
      max_uses: options.max_uses,
    });
    if (res?.success) {
      return {
        success: true,
        code: res.payload?.code,
        short_url: res.payload?.short_url,
        expires_at: res.payload?.expires_at,
      };
    }
    return { success: false, message: res?.message || 'Could not create share link.' };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const fetchCredentialShares = (
  projectId: number | string,
) => async (): Promise<{ success: boolean; shares: any[] }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const res = await apiGet(`/links/get-project-links/${accountId}/${cognitoId}/${projectId}`);
    const all = Array.isArray(res?.payload) ? res.payload : [];
    const nowSec = Math.floor(Date.now() / 1000);
    const shares = all.filter((l: any) => {
      if (l.targetType !== 'credential_share') return false;
      const notExpired = !l.expiresAt || l.expiresAt > nowSec;
      const notExhausted = !l.maxUses || l.hitCount < l.maxUses;
      return notExpired && notExhausted;
    });
    return { success: true, shares };
  } catch {
    return { success: false, shares: [] };
  }
};

export const revokeCredentialShare = (
  code: string,
) => async (): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const res = await apiDelete(`/links/revoke/${accountId}/${cognitoId}/${code}`);
    return { success: res?.success === true, message: res?.message };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};
